import { ref, watch } from 'vue';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';
import { usePostSaleCaseTopics } from './usePostSaleCaseTopics';

const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export function usePostSaleAnalysis({ postSaleCaseId, cardId, t, notify }) {
  const postSaleCase = ref(null);
  const transaction = ref(null);
  const loadingCase = ref(false);
  const loadingTransaction = ref(false);
  const updatingStatus = ref(false);
  const topics = usePostSaleCaseTopics({ t, notify });

  const openDeepLink = () => {
    const selected = topics.topics.value.find(
      topic => String(topic.id) === String(cardId.value || '')
    );
    if (selected) topics.openEdit(selected);
  };

  const load = async () => {
    if (!postSaleCaseId.value) return;
    loadingCase.value = true;
    transaction.value = null;

    try {
      const { data } = await PostSaleCasesAPI.show(postSaleCaseId.value);
      postSaleCase.value = data;
      loadingTransaction.value = true;
      const transactionRequest = SalesTransactionsAPI.show(
        data.property_transaction_id
      );
      const [, transactionResponse] = await Promise.all([
        topics.load(data.id),
        transactionRequest,
      ]);
      transaction.value = transactionResponse.data;
      openDeepLink();
    } catch (error) {
      postSaleCase.value = null;
      transaction.value = null;
      notify(messageFrom(error, t('POST_SALE.TOPICS.ALERTS.LOAD_ERROR')));
    } finally {
      loadingCase.value = false;
      loadingTransaction.value = false;
    }
  };

  const save = payload => topics.save(postSaleCaseId.value, payload);
  const remove = topic => topics.remove(postSaleCaseId.value, topic);

  const advanceToAnalysis = async () => {
    if (!postSaleCase.value) return null;
    if (postSaleCase.value.status === 'analise') return postSaleCase.value;

    updatingStatus.value = true;
    try {
      const { data } = await PostSaleCasesAPI.transition(
        postSaleCase.value.id,
        { status: 'analise' }
      );
      postSaleCase.value = data;
      notify(t('POST_SALE.ALERTS.STATUS_UPDATED'));
      return data;
    } catch (error) {
      notify(messageFrom(error, t('POST_SALE.ALERTS.LOAD_ERROR')));
      return null;
    } finally {
      updatingStatus.value = false;
    }
  };

  watch(cardId, openDeepLink);
  watch(postSaleCaseId, load);

  return {
    ...topics,
    advanceToAnalysis,
    load,
    loadingCase,
    loadingTransaction,
    postSaleCase,
    remove,
    save,
    transaction,
    updatingStatus,
  };
}
